import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { isStructureProduct } from "~/utils/product/structureProduct";

export function useCatalogAddToCart() {
	const cartStore = useCartStore();
	const { addToCart } = useGtmEvents();
	const { buildDepositChild } = useDepositChild();
	const { productTypes } = storeToRefs(useNorceStore());
	const { fetchProductTypes } = useProductTypes();
	const localePath = useLocalePath();
	const router = useRouter();
	const { $toast } = useNuxtApp();
	const { t } = useI18n();

	// Only where packs are configurable: with the feature off a bundle behaves
	// as it always has, and the lookup below would cost every quick add.
	const { packsEnabled } = usePacksEnabled();

	// A listing record has no `Type`, so a bundle looks simple until resolved —
	// one lookup on the click, answered from Redis. Why it matters:
	// `utils/product/structureProduct.ts`.
	const isStructure = async (item: ProductItem) => {
		if (!packsEnabled) return false;

		const known = isStructureProduct(item, productTypes.value);
		if (known !== undefined) return known;
		if (!item.UniqueName) return false;

		try {
			const { cultureCode, localeCode } = useNorceStore().storeContext;
			const [full] = await Promise.all([
				useNorcePwa().getProductByUniqueName({
					uniqueName: item.UniqueName,
					cultureCode,
					localeCode
				}),
				fetchProductTypes()
			]);
			return isStructureProduct(full, productTypes.value) === true;
		} catch (error) {
			// Add nothing rather than add a bundle blind — and say so.
			console.error(
				`[quick-buy] could not resolve the type of ${item.PartNo}`,
				error
			);
			$toast.error(t("add_to_cart_failed"));
			return "unknown" as const;
		}
	};

	const add = async (item: ProductItem) => {
		const structure = await isStructure(item);
		if (structure === "unknown") {
			return;
		}
		if (structure) {
			// Its contents are chosen on the product page.
			if (item.UniqueName) {
				await router.push(localePath(`/${item.UniqueName}`));
			}
			return;
		}

		const depositChild = await buildDepositChild(item, 1);
		await cartStore.addToCart(
			{
				// No `PriceListId`: the basket call carries the shopper's
				// `pricelistSeed`, which is what prices the line.
				PartNo: item.PartNo!,
				Quantity: 1,
				Name: item.VariantName || item.Name,
				Comment: null
			},
			depositChild
		);
		addToCart(item);
	};

	return { add };
}
